import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

import { AuthenticationStatus } from '@front/auth/src/authentication-context';

import { ReactComponent as LogoW } from '@front/common/src/assets/images/logo-w.svg';
import { ReactComponent as LogoWecasa } from '@front/common/src/assets/images/logo-wecasa.svg';
import colors from '@front/common/src/utils/colors';
import { UniverseReference, User } from '@front/common/src/types/commons';

import { translate } from '@front/i18n';

import { DesktopAndTablet, Mobile } from '@front/web-ui/src/styles/media-queries';
import Button from '@front/web-ui/src/components/button/button';
import { minWidth } from '@front/web-ui/src/styles/mixins';
import { DESKTOP_GRID_WIDTH } from '@front/web-ui/src/constants/layout';
import Icon from '@front/web-ui/src/components/icon/icon';
import { StylelessButton } from '@front/web-ui/src/components/utils';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import { getBackTitle, route, SIGN_IN_PATH } from '../../steps/steps';
import { trackSignInEvent } from '../../utils/tracking';

import { isLoginOrSignup } from './utils';

export const HEADER_HEIGHT = 58;

const PATHS_WITHOUT_CONNECT = [route(SIGN_IN_PATH)];

const Wrapper = styled.div`
  height: ${HEADER_HEIGHT}px;
  background-color: ${colors.white100};
  display: flex;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 11;
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 0 20px;

  ${minWidth.xl`
    width: ${DESKTOP_GRID_WIDTH}px;
  `};
`;

const StyledIcon = styled(Icon)`
  transition: all 0.3s ease-in-out;
  margin-right: 10px;
`;

const BackButton = styled(StylelessButton)`
  display: flex;
  align-items: center;
`;

const Connect = styled.div`
  max-width: 40%;
  text-align: right;
`;

const logoStyle = css`
  position: absolute;
  inset: 0;
  margin: auto;
  height: 18px;
`;

const StyledLogoW = styled(LogoW)`
  ${logoStyle}
`;

const StyledLogoWecasa = styled(LogoWecasa)`
  ${logoStyle}
`;

const t = translate('screens.main_container');

export type HeaderProps = {
  authenticationStatus: AuthenticationStatus;
  user?: User;
  openLoginScreen: () => void;
  universe: UniverseReference;
};

const Header = ({ authenticationStatus, user, openLoginScreen, universe }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const onBack = useCallback(() => {
    if (isLoginOrSignup(location.pathname)) {
      trackSignInEvent({
        eventType: 'BackButtonClick',
        universe,
        path: location.pathname,
      });
    }
    navigate(-1);
  }, [location.pathname, universe, navigate]);
  const { siteUrl, locale } = window.wecasa;
  const title = getBackTitle(universe, location);

  return (
    <Wrapper>
      <Container>
        {title && (
          <BackButton onClick={onBack} title={title}>
            <StyledIcon name='back' size={12} color={colors.ink100} />
            <Text.ButtonSM>{title}</Text.ButtonSM>
          </BackButton>
        )}
        <a href={`${siteUrl}${locale}`}>
          <Mobile>
            <StyledLogoW />
          </Mobile>
          <DesktopAndTablet>
            <StyledLogoWecasa />
          </DesktopAndTablet>
        </a>
        <Connect>
          {authenticationStatus === 'authenticated' && (
            <>{user?.first_name && <Text>{t('.hello', { firstName: user.first_name })}</Text>}</>
          )}
          {authenticationStatus === 'unauthenticated' && !PATHS_WITHOUT_CONNECT.includes(location.pathname) && (
            <Button onPress={openLoginScreen} variant='secondary' size='sm'>
              {t('.connect')}
            </Button>
          )}
        </Connect>
      </Container>
    </Wrapper>
  );
};

export default Header;
